import { Injectable, signal, Signal, computed } from '@angular/core';
import { GOOGLE_MAPS_API_KEY } from '../configs/common.config';

declare const google: any;

/**
 * Service to dynamically load Google Maps API
 * Only loads when needed, not on every page
 */
@Injectable({
  providedIn: 'root',
})
export class GoogleMapsLoaderService {
  private readonly _isLoading = signal<boolean>(false);
  private readonly _isLoaded = signal<boolean>(false);
  private readonly _error = signal<string | null>(null);

  public readonly isLoading: Signal<boolean> = computed(() => this._isLoading());
  public readonly isLoaded: Signal<boolean> = computed(() => this._isLoaded());
  public readonly error: Signal<string | null> = computed(() => this._error());

  /**
   * Google Maps API configuration
   * API key stored in common.config.ts for centralized management
   */
  private readonly GOOGLE_MAPS_CONFIG = {
    apiKey: GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
    version: '3',
  };

  /**
   * Loads Google Maps API dynamically
   * Returns a promise that resolves when the API is ready
   */
  public async loadGoogleMaps(): Promise<void> {
    // If already loaded, return immediately
    if (this._isLoaded()) {
      return Promise.resolve();
    }

    // If currently loading, wait for it
    if (this._isLoading()) {
      return this.waitForLoad();
    }

    this._isLoading.set(true);
    this._error.set(null);

    try {
      // Check if Google Maps is already loaded (from index.html or elsewhere)
      if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
        this._isLoaded.set(true);
        this._isLoading.set(false);
        return Promise.resolve();
      }

      // Create script element
      const script = document.createElement('script');
      const { apiKey, libraries } = this.GOOGLE_MAPS_CONFIG;

      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries.join(',')}`;
      script.async = true;
      script.defer = true;

      // Wait for script to load
      await new Promise<void>((resolve, reject) => {
        script.onload = () => {
          // Wait for google.maps to be fully initialized
          const checkGoogleMaps = setInterval(() => {
            if (typeof google !== 'undefined' && google.maps && google.maps.Map) {
              clearInterval(checkGoogleMaps);
              this._isLoaded.set(true);
              this._isLoading.set(false);
              resolve();
            }
          }, 100);

          // Timeout after 10 seconds
          setTimeout(() => {
            clearInterval(checkGoogleMaps);
            if (!this._isLoaded()) {
              const errorMsg = 'Timeout waiting for Google Maps to initialize';
              this._error.set(errorMsg);
              this._isLoading.set(false);
              reject(new Error(errorMsg));
            }
          }, 10000);
        };

        script.onerror = () => {
          const errorMsg = 'Failed to load Google Maps API';
          this._error.set(errorMsg);
          this._isLoading.set(false);
          reject(new Error(errorMsg));
        };

        document.head.appendChild(script);
      });

      return Promise.resolve();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error loading Google Maps';
      this._error.set(errorMessage);
      this._isLoading.set(false);
      throw error;
    }
  }

  /**
   * Waits for Google Maps to finish loading
   * Used when multiple components request the API simultaneously
   */
  private waitForLoad(): Promise<void> {
    return new Promise((resolve, reject) => {
      const checkInterval = setInterval(() => {
        if (this._isLoaded()) {
          clearInterval(checkInterval);
          resolve();
        } else if (this._error()) {
          clearInterval(checkInterval);
          reject(new Error(this._error() || 'Failed to load Google Maps'));
        }
      }, 100);

      // Timeout after 10 seconds
      setTimeout(() => {
        clearInterval(checkInterval);
        reject(new Error('Timeout loading Google Maps API'));
      }, 10000);
    });
  }

  /**
   * Checks if Google Maps API is available
   */
  public isGoogleMapsAvailable(): boolean {
    return typeof google !== 'undefined' && !!google.maps;
  }
}
